import { ReactNode, useCallback, useState } from 'react';
import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import Iconify from '../../iconify';
import CustomTooltip from './CustomTooltip';
import { PresenceSegments } from '../types';

type TooltipLabels = {
  organicOnly?: string;
  soloBidder?: string;
  topAdPosition?: string;
  competitorsOnly?: string;
};

type TooltipIcons = {
  organicOnly?: string | ReactNode;
  soloBidder?: string | ReactNode;
  topAdPosition?: string | ReactNode;
  competitorsOnly?: string | ReactNode;
};

type PresenceRateChartProps = {
  data: PresenceSegments;
  height?: number;
  totalVisibilityRate?: number;
  colors?: string[];
  round?: number;
  title?: string;
  tooltipDate?: string;
  labels?: TooltipLabels;
  icons?: TooltipIcons;
  sx?: SxProps<Theme>;
  barSx?: SxProps<Theme>;
  segmentSx?: SxProps<Theme>;
};

const calculateCompetitorsOnly = (totalVisibilityRate: number) => {
  return 100 - totalVisibilityRate;
};

export default function PresenceRateChart({
  data,
  round = 0,
  height = 6,
  totalVisibilityRate,
  colors: customColors,
  title = 'Presence breakdown',
  tooltipDate,
  labels,
  icons,
  sx,
  barSx,
  segmentSx,
}: PresenceRateChartProps) {
  const defaultColors = [
    '#18A155',
    '#C09910',
    '#F9DE81',
    'rgba(145, 158, 171, 0.20)',
  ];

  const defaultLabels = {
    organicOnly: 'Organic only',
    soloBidder: 'Solo bidder',
    topAdPosition: 'Top ad position',
    competitorsOnly: 'Competitors only',
  };

  const defaultIcons = {
    organicOnly: 'solar:global-linear',
    soloBidder: 'carbon:user',
    topAdPosition: 'carbon:user-multiple',
    competitorsOnly: 'ri:sword-line',
  };

  const tooltipLabels = {
    ...defaultLabels,
    ...labels,
  };

  const tooltipIcons = {
    ...defaultIcons,
    ...icons,
  };

  const colors =
    customColors && customColors.length === 4
      ? customColors
      : defaultColors;

  const visibilityBase =
    typeof totalVisibilityRate === 'number'
      ? totalVisibilityRate
      : 100;

  const rawItems = [
    {
      key: 'organicOnly',
      label: tooltipLabels.organicOnly,
      value: Math.max(0, data.organicOnly),
      color: colors[0],
      icon: tooltipIcons.organicOnly,
    },
    {
      key: 'soloBidder',
      label: tooltipLabels.soloBidder,
      value: Math.max(0, data.soloBidder),
      color: colors[1],
      icon: tooltipIcons.soloBidder,
    },
    {
      key: 'topAdPosition',
      label: tooltipLabels.topAdPosition,
      value: Math.max(0, data.topAdPosition),
      color: colors[2],
      icon: tooltipIcons.topAdPosition,
    },
    {
      key: 'competitorsOnly',
      label: tooltipLabels.competitorsOnly,
      value: Math.max(
        0,
        calculateCompetitorsOnly(visibilityBase)
      ),
      color: colors[3],
      icon: tooltipIcons.competitorsOnly,
    },
  ];

  const items = rawItems.map((it) => ({
    ...it,
    value:
      round === 0
        ? Math.round(it.value)
        : Number(it.value.toFixed(round)),
  }));

  const visibleItems = items.filter((it) => it.value > 0);

  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const renderIcon = (
    icon: string | ReactNode,
    color: string
  ) => {
    if (typeof icon === 'string') {
      return (
        <Iconify
          icon={icon}
          sx={{
            width: 14,
            height: 14,
            color,
            flexShrink: 0,
          }}
        />
      );
    }

    return (
      <Box
        sx={{
          width: 14,
          height: 14,
          color,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& svg': {
            width: 14,
            height: 14,
          },
        }}
      >
        {icon}
      </Box>
    );
  };

  return (
    <CustomTooltip
      placement="top"
      disableInteractive
      noPadding
      open={open}
      onClose={handleClose}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 'none',
            width: '320px',
            boxSizing: 'border-box',
            p: 0,
          },
        },
      }}
      title={
        <Stack direction="column">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              pt: '12px',
              px: '16px',
              pb: 0,
              gap: '8px',
              alignSelf: 'stretch',
            }}
          >
            <Typography
              sx={{
                color: '#333C47',
                fontFamily: '"Public Sans"',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '100%',
              }}
            >
              {title}
            </Typography>

            {tooltipDate && (
              <Typography
                sx={{
                  color: '#637381',
                  fontFamily: '"Public Sans"',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '100%',
                  whiteSpace: 'nowrap',
                }}
              >
                {tooltipDate}
              </Typography>
            )}
          </Stack>

          <Stack
            direction="column"
            sx={{
              pt: '12px',
              px: '16px',
              pb: '12px',
            }}
          >
            {items.map((it) => (
              <Stack
                key={it.key}
                direction="row"
                alignItems="center"
                sx={{
                  width: '100%',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {renderIcon(it.icon, it.color)}

                  <Typography
                    sx={{
                      color: '#333C47',
                      fontFamily: '"Public Sans"',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: '22px',
                    }}
                  >
                    {it.label}
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    color: '#637381',
                    fontVariantNumeric: 'lining-nums tabular-nums',
                    fontFamily: '"Public Sans"',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '22px',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {it.value}%
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      }
    >
      <Box
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        sx={[
          {
            width: '100%',
            minWidth: 0,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <Box
          aria-label="presence-rate-bar"
          sx={[
            {
              display: 'flex',
              gap: '2px',
              width: '100%',
              height: `${height}px`,
              borderRadius: '2px',
              overflow: 'hidden',
            },
            ...(Array.isArray(barSx) ? barSx : [barSx]),
          ]}
        >
          {visibleItems.map((it) => (
            <Box
              key={it.key}
              sx={[
                {
                  flex: it.value,
                  height: '100%',
                  backgroundColor: it.color,
                  transition: 'all 0.2s ease',
                  borderRadius: '2px',
                  minWidth: 2,
                },
                ...(Array.isArray(segmentSx) ? segmentSx : [segmentSx]),
              ]}
            />
          ))}
        </Box>
      </Box>
    </CustomTooltip>
  );
}